// What this browser remembers between visits (SPEC-redesign §4.6). The prototype
// promises „Adres strony pamięta wszystko." and stores nothing; here it is a rule.
//
// Every key is wrapped, because `localStorage` throws rather than returns in a
// locked-down or private window, and a boot that dies on a storage read is a
// worse failure than a forgotten radius.
import type { Voice } from '../shared/model.ts';
import type { Order, ViewState } from './viewstate.ts';

/** Mirrors VOICES in src/state/viewstate.ts; the two are asserted equal in the tests. */
const VOICES = ['ztm', 'ztmk', 'pl', 'aq'] as const satisfies readonly Voice[];

/** Mirrors DEFAULT_VIEW.radius in src/state/viewstate.ts; the two are asserted equal in the tests. */
const DEFAULT_RADIUS = 3000;
/** Half way up: the default block of 363 px in the 92…633 px range (§5.3, D-h). */
const DEFAULT_GRIP = 0.5;

/** Mirrors DEFAULT_VIEW.order in src/state/hash.ts; the two are asserted equal in the tests. */
const DEFAULT_ORDER: Order = 'czas';
const ORDERS = ['czas', 'odleglosc'] as const satisfies readonly Order[];

function read(key: string): string | null {
  try {
    return localStorage.getItem(`poznan-teraz:${key}`);
  } catch {
    return null;
  }
}

function save(key: string, value: string) {
  try {
    localStorage.setItem(`poznan-teraz:${key}`, value);
  } catch {
    /* private mode, or a quota that is already full */
  }
}

function number(key: string, fallback: number, min: number, max: number): number {
  const raw = read(key);
  const n = Number(raw);
  // `Number(null)` is 0, which is a perfectly finite wrong answer: nothing stored
  // has to reach the fallback, not the bottom of the range.
  if (raw === null || raw.trim() === '' || !Number.isFinite(n)) return fallback;
  return Math.min(max, Math.max(min, n));
}

function list(key: string): string[] {
  const raw = read(key);
  if (!raw) return [];
  try {
    const parsed: unknown = JSON.parse(raw);
    return Array.isArray(parsed) ? parsed.map(String).filter(Boolean) : [];
  } catch {
    return [];
  }
}

/** The same pair against the tab's own store, which no visit inherits. */
function readForNow(key: string): string | null {
  try {
    return sessionStorage.getItem(`poznan-teraz:${key}`);
  } catch {
    return null;
  }
}

function saveForNow(key: string, value: string) {
  try {
    sessionStorage.setItem(`poznan-teraz:${key}`, value);
  } catch {
    /* private mode, or a quota that is already full */
  }
}

export const memory = {
  grip: {
    get: (): number => number('grip', DEFAULT_GRIP, 0, 1),
    set: (v: number) => save('grip', String(v)),
  },
  lines: {
    get: (): string[] => list('lines'),
    set: (v: string[]) => save('lines', JSON.stringify(v)),
  },
  muted: {
    get: (): Voice[] => list('cisza').filter((v): v is Voice => VOICES.some((k) => k === v)),
    set: (v: Voice[]) => save('cisza', JSON.stringify(v)),
  },
  radius: {
    get: (): number => number('r', DEFAULT_RADIUS, 200, 5000),
    set: (v: number) => save('r', String(v)),
  },
  order: {
    get: (): Order => ORDERS.find((o) => o === read('uklad')) ?? DEFAULT_ORDER,
    set: (v: Order) => save('uklad', v),
  },
  runs: {
    get: (): number => number('runs', 0, 0, Number.MAX_SAFE_INTEGER),
    bump: () => save('runs', String(memory.runs.get() + 1)),
  },
  /**
   * „Dodaj do ekranu" dismissed, once and for good (#348, Decision 50 d). This
   * browser's own answer and nothing else's, so it is here and never in the
   * address: a link sent to a friend may not hide their install band.
   */
  install: {
    dismissed: (): boolean => read('install') === 'ukryty',
    dismiss: () => save('install', 'ukryty'),
    /**
     * The browser's own install dialog, cancelled. This TAB's answer and only
     * this tab's — `sessionStorage`, so it dies with the tab — because saying
     * no to that dialog is not the same as saying no to the band (#348).
     */
    cancelled: (): boolean => readForNow('install') === 'anulowane',
    cancel: () => saveForNow('install', 'anulowane'),
  },
};

/**
 * What this browser would restore into a view that came from a bare URL. A pasted
 * link always wins: a field the address carries is never overwritten from memory,
 * so a link sent to a friend opens the same screen on their phone as on yours.
 *
 * The caller applies it with `'replace'` — restoring is not an opening.
 */
export function remembered(view: ViewState): Partial<ViewState> {
  const patch: Partial<ViewState> = {};
  if (view.lines.length === 0) {
    const lines = memory.lines.get();
    if (lines.length > 0) patch.lines = lines;
  }
  if (view.muted.length === 0) {
    const muted = memory.muted.get();
    if (muted.length > 0) patch.muted = muted;
  }
  if (view.radius === DEFAULT_RADIUS) {
    const radius = memory.radius.get();
    if (radius !== DEFAULT_RADIUS) patch.radius = radius;
  }
  // The rail's order, under the radius's own rule (§2.3). It inherits #241 D5
  // with it — a link that omits `uklad` opens in the visitor's remembered
  // order — deliberately, so D5 stays one question about one rule.
  if (view.order === DEFAULT_ORDER) {
    const order = memory.order.get();
    if (order !== DEFAULT_ORDER) patch.order = order;
  }
  return patch;
}
